const MAX_ON_ZOOM_SELECTOR = "img[data-max-on-zoom-src]";
const ZOOM_SCALE_THRESHOLD = 1.3;

type UpgradeState = "loading" | "loaded" | "failed";

const upgradeStates = new WeakMap<HTMLImageElement, UpgradeState>();

const toAbsoluteUrl = (src: string): string => {
  try {
    return new URL(src, window.location.href).href;
  } catch {
    return src;
  }
};

const isVisibleInVisualViewport = (
  img: HTMLImageElement,
  viewport: VisualViewport,
): boolean => {
  const rect = img.getBoundingClientRect();
  const viewportRight = viewport.offsetLeft + viewport.width;
  const viewportBottom = viewport.offsetTop + viewport.height;

  return (
    rect.width > 0 &&
    rect.height > 0 &&
    rect.right > viewport.offsetLeft &&
    rect.left < viewportRight &&
    rect.bottom > viewport.offsetTop &&
    rect.top < viewportBottom
  );
};

const lockToMaxImage = (img: HTMLImageElement, maxSrc: string) => {
  // While srcset is present, the browser remains in control of the selected
  // candidate and may ignore a new src value.
  img.removeAttribute("srcset");
  img.removeAttribute("sizes");
  img.src = maxSrc;
  upgradeStates.set(img, "loaded");
};

const preloadAndLockToMax = (img: HTMLImageElement, maxSrc: string) => {
  if (upgradeStates.has(img)) {
    return;
  }

  const absoluteMaxSrc = toAbsoluteUrl(maxSrc);
  if (toAbsoluteUrl(img.currentSrc || img.src) === absoluteMaxSrc) {
    lockToMaxImage(img, maxSrc);
    return;
  }

  upgradeStates.set(img, "loading");

  const preloader = new Image();
  preloader.decoding = "async";
  preloader.onload = async () => {
    try {
      await preloader.decode();
    } catch {}

    if (img.isConnected) {
      lockToMaxImage(img, maxSrc);
    }
  };
  preloader.onerror = () => {
    upgradeStates.set(img, "failed");
  };
  preloader.src = maxSrc;
};

const upgradeVisibleImages = () => {
  const viewport = window.visualViewport;
  if (!viewport || viewport.scale <= ZOOM_SCALE_THRESHOLD) {
    return;
  }

  document
    .querySelectorAll<HTMLImageElement>(MAX_ON_ZOOM_SELECTOR)
    .forEach((img) => {
      const maxSrc = img.dataset.maxOnZoomSrc;
      if (
        maxSrc &&
        !upgradeStates.has(img) &&
        isVisibleInVisualViewport(img, viewport)
      ) {
        preloadAndLockToMax(img, maxSrc);
      }
    });
};

let upgradeAnimationFrame: number | null = null;

const scheduleVisibleImageUpgrade = () => {
  if (upgradeAnimationFrame !== null) {
    return;
  }

  upgradeAnimationFrame = window.requestAnimationFrame(() => {
    upgradeAnimationFrame = null;
    upgradeVisibleImages();
  });
};

window.visualViewport?.addEventListener(
  "resize",
  scheduleVisibleImageUpgrade,
);
window.visualViewport?.addEventListener(
  "scroll",
  scheduleVisibleImageUpgrade,
);
window.addEventListener("scroll", scheduleVisibleImageUpgrade, {
  passive: true,
});
window.addEventListener("orientationchange", scheduleVisibleImageUpgrade);

scheduleVisibleImageUpgrade();

const initVisualViewportDebugOverlay = () => {
  const debugValue = new URLSearchParams(window.location.search).get(
    "debugZoom",
  );
  if (debugValue !== "1" && debugValue !== "true") {
    return;
  }

  const panel = document.createElement("div");
  panel.setAttribute("role", "status");
  panel.setAttribute("aria-label", "Visual viewport zoom debug information");

  Object.assign(panel.style, {
    position: "fixed",
    left: "8px",
    top: "8px",
    zIndex: "2147483647",
    boxSizing: "border-box",
    minWidth: "190px",
    padding: "10px 12px",
    border: "2px solid #fff",
    borderRadius: "8px",
    background: "rgba(20, 20, 20, 0.92)",
    color: "#fff",
    font: "12px/1.45 monospace",
    whiteSpace: "pre",
    pointerEvents: "none",
    transformOrigin: "top left",
    boxShadow: "0 2px 12px rgba(0, 0, 0, 0.35)",
  });

  document.body.append(panel);

  const viewport = window.visualViewport;
  let lastEvent = "initial";
  let eventCount = 0;
  let animationFrameId: number | null = null;

  const render = () => {
    animationFrameId = null;

    if (!viewport) {
      panel.textContent =
        "VisualViewport: unsupported\n" +
        `event: ${lastEvent}\n` +
        "scale: unavailable";
      panel.style.background = "rgba(150, 25, 25, 0.94)";
      return;
    }

    const scale = viewport.scale;
    const zoomed = scale > ZOOM_SCALE_THRESHOLD;
    const inverseScale = scale > 0 ? 1 / scale : 1;
    const zoomImages = Array.from(
      document.querySelectorAll<HTMLImageElement>(MAX_ON_ZOOM_SELECTOR),
    );
    const loadedImages = zoomImages.filter(
      (img) => upgradeStates.get(img) === "loaded",
    ).length;

    panel.style.left = `${viewport.offsetLeft + 8 / scale}px`;
    panel.style.top = `${viewport.offsetTop + 8 / scale}px`;
    panel.style.transform = `scale(${inverseScale})`;
    panel.style.background = zoomed
      ? "rgba(13, 110, 55, 0.94)"
      : "rgba(20, 20, 20, 0.92)";

    panel.textContent = [
      "VisualViewport: supported",
      `event: ${lastEvent} #${eventCount}`,
      `scale: ${scale.toFixed(3)}`,
      `zoomed: ${zoomed ? "YES" : "NO"}`,
      `max images: ${loadedImages}/${zoomImages.length}`,
      `size: ${viewport.width.toFixed(1)} × ${viewport.height.toFixed(1)}`,
      `offset: ${viewport.offsetLeft.toFixed(1)}, ${viewport.offsetTop.toFixed(1)}`,
      `page: ${viewport.pageLeft.toFixed(1)}, ${viewport.pageTop.toFixed(1)}`,
      `devicePixelRatio: ${window.devicePixelRatio.toFixed(2)}`,
    ].join("\n");
  };

  const scheduleRender = (eventName: string) => {
    lastEvent = eventName;
    eventCount += 1;

    if (animationFrameId === null) {
      animationFrameId = window.requestAnimationFrame(render);
    }
  };

  viewport?.addEventListener("resize", () => {
    scheduleRender("visualViewport.resize");
  });
  viewport?.addEventListener("scroll", () => {
    scheduleRender("visualViewport.scroll");
  });
  window.addEventListener(
    "scroll",
    () => scheduleRender("window.scroll"),
    { passive: true },
  );
  window.addEventListener("orientationchange", () => {
    scheduleRender("orientationchange");
  });

  render();
};

initVisualViewportDebugOverlay();
